import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'viewer',
    is_active: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchUser = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        const response = await userApi.getById(id);
        
        // Remove password field for edit mode
        const userData = response.data;
        delete userData.password;
        
        setFormData(userData);
        setError(null);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
  }, [id, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    
    // Validate form
    if (!formData.username) {
      setError('Username is required');
      return;
    }
    
    if (!isEditMode && !formData.password) {
      setError('Password is required for new users');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Create submission data (don't send empty password in edit mode)
      const submissionData = { ...formData };
      if (isEditMode && !submissionData.password) {
        delete submissionData.password;
      }
      
      if (isEditMode) {
        await userApi.update(id, submissionData);
      } else {
        await userApi.create(submissionData);
      }
      
      // Redirect to user list
      navigate('/users');
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.detail || 'Failed to save user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Check permissions
  if ((isEditMode && !hasPermission('users', 'update')) || 
      (!isEditMode && !hasPermission('users', 'create'))) {
    return (
      <div className="alert alert-danger" role="alert">
        You don't have permission to {isEditMode ? 'edit' : 'create'} users.
      </div>
    );
  }
  
  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isEditMode ? 'Edit User' : 'Create User'}</h1>
      </div>
      
      <div className="card shadow">
        <div className="card-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">Username *</label>
              <input
                type="text"
                className={`form-control ${formSubmitted && !formData.username ? 'is-invalid' : ''}`}
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
                required
              />
              {formSubmitted && !formData.username && (
                <div className="invalid-feedback">Username is required</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                {isEditMode ? 'Password (leave blank to keep current)' : 'Password *'}
              </label>
              <input
                type="password"
                className={`form-control ${formSubmitted && !isEditMode && !formData.password ? 'is-invalid' : ''}`}
                id="password"
                name="password"
                value={formData.password || ''}
                onChange={handleChange}
                disabled={loading}
                required={!isEditMode}
              />
              {formSubmitted && !isEditMode && !formData.password && (
                <div className="invalid-feedback">Password is required for new users</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="role" className="form-label">Role *</label>
              <select
                className="form-select"
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={loading}
                required
              >
                <option value="owner">Owner</option>
                <option value="admin">Administrator</option>
                <option value="member_manager">Member Manager</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            
            {isEditMode && (
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="is_active">Active</label>
              </div>
            )}
            
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Link to="/users" className="btn btn-secondary">
                Cancel
              </Link>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Save User'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
